import { Alert, Button, Group, Modal, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { type ReactNode, useState } from 'react';
import { CashOutOfRegister, useOpenRegisters } from '../../components/CashOutOfRegister';
import { enumOptions } from '../../lib/enums';
import { currencySymbol, formatMoney, toMajorUnits, toMinorUnits } from '../../lib/format';

/**
 * How far above what is owed a payout may go: one whole unit, L 1.00 or $ 1.00.
 *
 * The same figure as `CASH_ROUNDING_ALLOWANCE_MINOR` in
 * `backend/src/domains/suppliers/routes.ts`, which the referral route imports rather than restates.
 * There are no coins on this island: cash comes and goes in whole bills and settles upwards, and a
 * commission is a percentage of what a table spent, so it lands on centavos nearly every time —
 * 10% of L 2,437.50 is L 243.75 and what goes into a driver's hand is L 244.00. Held here as well
 * so this screen refuses exactly the set the API refuses, and the reason is beside the box that
 * caused it rather than arriving as a server error after the press.
 */
const CASH_ROUNDING_ALLOWANCE_MINOR = 100;

/**
 * Counting money out to a partner, wherever the reader happened to be looking at what they are owed.
 *
 * There are two places that question gets asked and they used to have one answer between them. The
 * payouts list could record a payment; the balances list — the screen that actually says a driver is
 * owed L 2,000 — could only start a payout for everybody and send you to the other tab to find his
 * line among theirs. Both now open this, so the wording, the threshold rule and the part-payment
 * arithmetic cannot drift apart between the screen that shows the debt and the screen that clears it.
 */

export interface PayoutPayment {
  methodUsed: 'cash' | 'transfer';
  reference: string | null;
  amountMinor: number;
  /**
   * The register the notes came out of, on a cash handover. Null on a transfer, which touches no drawer.
   *
   * Counting a driver's commission out of the register now takes it out of the register: the server records
   * a paid-out against this drawer and its expected balance comes down. Until it did, the bartender
   * counting that drawer at the end of the night came up short by exactly what the driver had
   * walked away with hours earlier.
   */
  drawerSessionId: string | null;
}

export interface PayoutPaymentModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  /** What is still owed, in minor units — the amount the box opens on. */
  outstandingMinor: number;
  currency: string;
  /**
   * Commission that has not reached the minimum set for this partner. Cash can always be handed
   * over; a transfer is refused by the ledger, so it is offered as disabled rather than as an option
   * that fails on save.
   */
  belowThreshold?: boolean;
  /**
   * How this partner asked to be paid, where the caller knows it.
   *
   * A driver who is paid in cash is standing at the bar; opening his dialog on "Bank transfer"
   * is one unnoticed keystroke away from a cash handover recorded as a transfer that never left
   * the account. Falls back to transfer, which is what a payout line — carrying no preference of
   * its own — has always assumed.
   */
  defaultMethod?: string;
  /** Anything the reader needs to know before counting it out — what period it covers, say. */
  children?: ReactNode;
  submitting?: boolean;
  onSubmit: (payment: PayoutPayment) => void;
}

export function PayoutPaymentModal(props: PayoutPaymentModalProps) {
  const [method, setMethod] = useState<string>(
    props.belowThreshold ? 'cash' : (props.defaultMethod ?? 'transfer'),
  );
  const [amount, setAmount] = useState<number | string>(toMajorUnits(props.outstandingMinor));
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [drawerSessionId, setDrawerSessionId] = useState<string | null>(null);

  const cash = method !== 'transfer';
  const { registers, loading: registersLoading, noneOpen } = useOpenRegisters(props.opened && cash);
  // One open register needs no question answered twice; the picker still names it, because which
  // drawer is about to be lighter is worth reading before the money is counted out.
  const chosenRegister =
    drawerSessionId ?? (registers.length === 1 ? (registers[0]?.drawerSessionId ?? null) : null);
  const amountMinor = toMinorUnits(amount as number) ?? 0;

  /*
   * Two bands, not one, because a bigger number means two different things.
   *
   * Rounding up to the notes actually handed over is real and has to be recordable — the money that
   * left the register is the money that left the register. A typed extra zero is not: L 500.00 against a
   * L 300.00 commission used to be accepted by this screen and by the API, marked the line paid,
   * and took five hundred lempiras out of the drawer, which then counted off level because the
   * paid-out matched the payment exactly.
   */
  const overBy = amountMinor - props.outstandingMinor;
  const rounding = overBy > 0 && overBy <= CASH_ROUNDING_ALLOWANCE_MINOR;
  const tooMuch = overBy > CASH_ROUNDING_ALLOWANCE_MINOR;

  const submit = () => {
    if (!amountMinor || amountMinor <= 0) {
      setError('Enter how much you handed over.');
      return;
    }
    if (tooMuch) {
      setError('That is far more than they are owed — check the amount.');
      return;
    }
    if (cash && !chosenRegister) {
      setError('Say which register the cash came out of.');
      return;
    }
    props.onSubmit({
      methodUsed: cash ? 'cash' : 'transfer',
      reference: reference.trim() || null,
      amountMinor,
      drawerSessionId: cash ? chosenRegister : null,
    });
  };

  return (
    <Modal opened={props.opened} onClose={props.onClose} title={props.title} closeOnClickOutside={false}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {formatMoney(props.outstandingMinor, props.currency)} outstanding.
        </Text>
        {props.children}
        <Select
          label="How you paid them"
          data={enumOptions('payoutMethod').map((o) =>
            // A transfer under the minimum is refused by the ledger, so it is not offered: the
            // point of the minimum is not paying bank fees on a L 40 transfer.
            props.belowThreshold && o.value === 'transfer'
              ? { ...o, disabled: true, description: 'Not until they reach their minimum' }
              : o,
          )}
          value={method}
          onChange={(v) => setMethod(v ?? 'cash')}
          comboboxProps={{ withinPortal: true }}
          allowDeselect={false}
        />
        <NumberInput
          label="Amount handed over"
          description="Less than the full amount leaves the rest outstanding for next time."
          value={amount}
          onChange={(v) => {
            setAmount(v);
            setError(null);
          }}
          error={error}
          decimalScale={2}
          fixedDecimalScale
          thousandSeparator=","
          // A partner can be owed lempiras and dollars at once, on two separate lines. The box
          // has to say which one is being counted out here.
          prefix={`${currencySymbol(props.currency)} `}
          hideControls
        />
        {cash && (
          <CashOutOfRegister
            registers={registers}
            loading={registersLoading}
            value={chosenRegister}
            onChange={(id) => {
              setDrawerSessionId(id);
              setError(null);
            }}
            currency={props.currency}
            amountMinor={amountMinor}
            disabled={props.submitting}
          />
        )}
        <TextInput
          label="Reference"
          description="The transfer number, or whatever you wrote on the receipt — so this can be found again"
          value={reference}
          onChange={(e) => setReference(e.currentTarget.value)}
        />
        {/* Handing over more than somebody earned is how a typed extra zero is discovered weeks
            later, when the commission he was paid and the commission he earned disagree. */}
        {rounding && (
          <Alert color="yellow" variant="light" title="That is more than they are owed">
            {formatMoney(overBy, props.currency)} more than the{' '}
            {formatMoney(props.outstandingMinor, props.currency)} outstanding — about what rounding up to the
            notes you handed over would come to. Record it if that is genuinely what you gave them.
          </Alert>
        )}
        {tooMuch && (
          <Alert color="red" variant="light" title="That is far more than they are owed">
            {formatMoney(amountMinor, props.currency)} against{' '}
            {formatMoney(props.outstandingMinor, props.currency)} outstanding. Check the amount — a payment
            this far over is refused.
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={props.onClose} disabled={props.submitting}>
            Cancel
          </Button>
          {/* Blocked rather than allowed-and-refused: with no drawer open the money did not come
              out of a register, and the reason is on screen beside the choice that caused it. */}
          <Button onClick={submit} loading={props.submitting} disabled={cash && noneOpen}>
            Record it
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
